import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import { SettingsAPI } from "../../services/settings.service";
import { ImmunizationAPI, CohortEntry, CohortRemindResult, CohortOutcomeResult } from "../../services/immunization.service";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";

const WINDOW_OPTIONS = [
  { value: "3m", label: "Last 3 months" },
  { value: "6m", label: "Last 6 months" },
  { value: "12m", label: "Last 12 months" },
];

export default function CohortReminderPage() {
  const { isFeatureAccessible, loading: flagsLoading } = useFeatureFlags();
  const immunizationEnabled = isFeatureAccessible("immunization");
  const [clinicId, setClinicId] = useState<string | null>(null);
  const [missedSince, setMissedSince] = useState("6m");
  const [entries, setEntries] = useState<CohortEntry[]>([]);
  const [totalConsented, setTotalConsented] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<CohortRemindResult | null>(null);
  const [outcomes, setOutcomes] = useState<CohortOutcomeResult | null>(null);

  const loadOutcomes = useCallback(async (cId: string) => {
    try {
      setOutcomes(await ImmunizationAPI.getCohortOutcomes(cId));
    } catch {
      // Non-fatal - the outcomes panel just stays hidden if this fails.
    }
  }, []);

  const load = useCallback(async (cId: string, window: string) => {
    setLoading(true);
    try {
      const cohort = await ImmunizationAPI.getCohort(cId, window);
      setEntries(cohort.entries);
      setTotalConsented(cohort.total_consented);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load cohort");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const profile = await SettingsAPI.getProfile();
        const cId = (profile as { clinic_id?: string | null }).clinic_id;
        if (!cId) return;
        setClinicId(cId);
        await Promise.all([load(cId, missedSince), loadOutcomes(cId)]);
      } catch {
        toast.error("Failed to load clinic profile");
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWindowChange = async (value: string) => {
    setMissedSince(value);
    setLastResult(null);
    if (clinicId) await load(clinicId, value);
  };

  // Grouped by patient - the actual bulk send is one message per child (see
  // delivery_service.py's grouping), so the preview should read the same way
  // rather than showing one row per missed dose.
  const groupedEntries = useMemo(() => {
    const byPatient = new Map<string, {
      patient_id: string;
      patient_name: string;
      vaccineList: string;
      dueDates: string;
      guardian_whatsapp_number?: string | null;
      consented: boolean;
    }>();
    for (const e of entries) {
      const existing = byPatient.get(e.patient_id);
      const vaccineLabel = `${e.vaccine_name} (dose ${e.dose_number})`;
      if (existing) {
        if (!existing.vaccineList.includes(vaccineLabel)) {
          existing.vaccineList += `, ${vaccineLabel}`;
        }
        if (!existing.dueDates.includes(e.due_date)) {
          existing.dueDates += `, ${e.due_date}`;
        }
      } else {
        byPatient.set(e.patient_id, {
          patient_id: e.patient_id,
          patient_name: e.patient_name,
          vaccineList: vaccineLabel,
          dueDates: e.due_date,
          guardian_whatsapp_number: e.guardian_whatsapp_number,
          consented: e.consented,
        });
      }
    }
    return Array.from(byPatient.values());
  }, [entries]);

  const handleBulkRemind = async () => {
    if (!clinicId) return;
    setSending(true);
    try {
      const result = await ImmunizationAPI.remindCohort(clinicId, missedSince);
      setLastResult(result);
      toast.success(`Sent ${result.sent} of ${result.attempted} reminders`);
      await Promise.all([load(clinicId, missedSince), loadOutcomes(clinicId)]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk reminder failed");
    } finally {
      setSending(false);
    }
  };

  if (!flagsLoading && !immunizationEnabled) {
    // Not a payment gate (LockedFeatureEmptyState's "upgrade your plan" copy
    // would be actively misleading here) - this flag is off pending Sid's
    // clinical sign-off on the NIS/IAP schedule, independent of billing.
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar />
        <div className="lg:pl-[72px]">
          <AppHeader title="Missed Immunizations" />
          <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center">
              <p className="text-sm font-medium text-slate-800">
                Pediatric Vaccination Schedule isn't enabled for this clinic yet
              </p>
              <p className="text-xs text-slate-500 mt-1">
                This feature is pending clinical sign-off on the vaccination schedule
                data. Contact your admin if you believe this should be enabled.
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <div className="lg:pl-[72px]">
        <AppHeader title="Missed Immunizations" />
        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  Children who missed a vaccination
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Filters doses that were due and still not administered/skipped.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={missedSince}
                  onChange={(e) => handleWindowChange(e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
                >
                  {WINDOW_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleBulkRemind}
                  disabled={sending || totalConsented === 0}
                  className="text-sm font-medium bg-[#0066ff] text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {sending ? "Sending…" : `Send bulk reminder (${totalConsented})`}
                </button>
              </div>
            </div>

            {lastResult && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-slate-800">{lastResult.attempted}</div>
                  <div className="text-xs text-slate-500">Attempted</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-emerald-700">{lastResult.sent}</div>
                  <div className="text-xs text-slate-500">Sent</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-amber-700">{lastResult.skipped_no_consent}</div>
                  <div className="text-xs text-slate-500">No consent</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-slate-600">{lastResult.skipped_quiet_hours}</div>
                  <div className="text-xs text-slate-500">Quiet hours</div>
                </div>
                <div className="bg-rose-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-rose-700">{lastResult.skipped_insufficient_credits}</div>
                  <div className="text-xs text-slate-500">No credits</div>
                </div>
              </div>
            )}
          </div>

          {outcomes && outcomes.total_reminded > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-6">
              <h3 className="text-sm font-semibold text-slate-800">Response dashboard</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Of every dose ever sent a catch-up reminder from this screen, how many have
                since been administered — the tracked outcome signal.
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-slate-800">{outcomes.total_reminded}</div>
                  <div className="text-xs text-slate-500">Reminded (ever)</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-emerald-700">{outcomes.administered_after}</div>
                  <div className="text-xs text-slate-500">Administered after</div>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 text-center">
                  <div className="text-lg font-semibold text-amber-700">{outcomes.still_pending}</div>
                  <div className="text-xs text-slate-500">Still pending</div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-6 text-sm text-slate-500">Loading…</div>
            ) : groupedEntries.length === 0 ? (
              <div className="p-6 text-sm text-slate-500">
                No children have a missed dose in this window.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Child</th>
                    <th className="px-4 py-2">Missed vaccines</th>
                    <th className="px-4 py-2">Due date(s)</th>
                    <th className="px-4 py-2">Guardian contact</th>
                    <th className="px-4 py-2">Consent</th>
                  </tr>
                </thead>
                <tbody>
                  {/* One row per child - the actual bulk send is one message
                      per guardian, not one per missed dose (see delivery_service.py). */}
                  {groupedEntries.map((e) => (
                    <tr key={e.patient_id} className="border-t border-slate-100">
                      <td className="px-4 py-2">{e.patient_name}</td>
                      <td className="px-4 py-2">{e.vaccineList}</td>
                      <td className="px-4 py-2">{e.dueDates}</td>
                      <td className="px-4 py-2">{e.guardian_whatsapp_number || "—"}</td>
                      <td className="px-4 py-2">
                        {e.consented ? (
                          <span className="text-emerald-600 text-xs">Consented</span>
                        ) : (
                          <span className="text-slate-400 text-xs">Opted out</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
